export const MARKETING_ATTR_STORAGE_KEY = "jorvio_marketing_attribution_v1";
export const MARKETING_VISITOR_STORAGE_KEY = "jorvio_marketing_visitor_v1";
export const MARKETING_SESSION_STORAGE_KEY = "jorvio_marketing_session_v1";

export type MarketingAttribution = {
  landingPath: string;
  referrer: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  capturedAt: string;
  visitorId?: string | null;
  sessionId?: string | null;
};

const PUBLIC_HIT_PATHS = new Set(["/", "/signup", "/contact", "/login", "/terms", "/privacy", "/invite"]);

export type ContentMarketingKind = "guide" | "comparison" | "trade";

export function isPublicMarketingPath(path: string): boolean {
  const p = path.split("?")[0]?.trim() || "/";
  if (PUBLIC_HIT_PATHS.has(p)) return true;
  if (p.startsWith("/legal/")) return true;
  if (p.startsWith("/for/")) return true;
  if (p.startsWith("/compare/")) return true;
  if (p.startsWith("/guides/") || p === "/guides") return true;
  return false;
}

export function getContentMarketingKind(path: string): ContentMarketingKind | null {
  const p = path.split(/[?#]/)[0]?.trim() || "/";
  if (p.startsWith("/guides/")) return "guide";
  if (p.startsWith("/compare/")) return "comparison";
  if (p.startsWith("/for/")) return "trade";
  return null;
}

export function isContentMarketingPath(path: string): boolean {
  return getContentMarketingKind(path) !== null;
}

function createJourneyId(prefix: "v" | "s"): string {
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
  return `${prefix}_${random}`;
}

function readOrCreateStorageId(
  storage: Storage,
  key: string,
  prefix: "v" | "s"
): string {
  const existing = storage.getItem(key)?.trim();
  if (existing && existing.length >= 16 && existing.length <= 100) return existing;
  const next = createJourneyId(prefix);
  storage.setItem(key, next);
  return next;
}

export function getOrCreateMarketingJourneyIdentity(): {
  visitorId: string | null;
  sessionId: string | null;
} {
  if (typeof window === "undefined") return { visitorId: null, sessionId: null };
  try {
    return {
      visitorId: readOrCreateStorageId(window.localStorage, MARKETING_VISITOR_STORAGE_KEY, "v"),
      sessionId: readOrCreateStorageId(window.sessionStorage, MARKETING_SESSION_STORAGE_KEY, "s"),
    };
  } catch {
    return { visitorId: null, sessionId: null };
  }
}

export function parseMarketingAttributionFromLocation(
  pathname: string,
  search: string,
  documentReferrer: string
): MarketingAttribution {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const utmSource = params.get("utm_source")?.trim().slice(0, 120) || null;
  const utmMedium = params.get("utm_medium")?.trim().slice(0, 120) || null;
  const utmCampaign = params.get("utm_campaign")?.trim().slice(0, 120) || null;
  const ref = documentReferrer?.trim().slice(0, 500) || null;
  return {
    landingPath: pathname || "/",
    referrer: ref,
    utmSource,
    utmMedium,
    utmCampaign,
    capturedAt: new Date().toISOString(),
  };
}

export function readStoredMarketingAttribution(): MarketingAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(MARKETING_ATTR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MarketingAttribution;
    if (!parsed || typeof parsed.landingPath !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function storeMarketingAttribution(attr: MarketingAttribution): void {
  if (typeof window === "undefined") return;
  try {
    const existing = readStoredMarketingAttribution();
    if (existing) {
      if ((!existing.visitorId && attr.visitorId) || (!existing.sessionId && attr.sessionId)) {
        window.sessionStorage.setItem(
          MARKETING_ATTR_STORAGE_KEY,
          JSON.stringify({
            ...existing,
            visitorId: existing.visitorId ?? attr.visitorId ?? null,
            sessionId: existing.sessionId ?? attr.sessionId ?? null,
          })
        );
      }
      return;
    }
    window.sessionStorage.setItem(MARKETING_ATTR_STORAGE_KEY, JSON.stringify(attr));
  } catch {
    /* ignore */
  }
}

export function clearStoredMarketingAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(MARKETING_ATTR_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}